import {useQuery} from '@github-ui/react-query'
import {fetchCAPI} from '@github-ui/agent-sessions/utils/fetch-capi'

type AgenticAutofixJobStatus =
  | 'invalid'
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'timed_out'

const terminalStatuses: AgenticAutofixJobStatus[] = ['invalid', 'completed', 'failed', 'cancelled', 'timed_out']

export type AgenticAutofixJob = {
  job_id: string
  status?: AgenticAutofixJobStatus
  pull_request?: {id: number; number: number}
  error?: {message: string}
}

function isTerminal(job: AgenticAutofixJob) {
  return (job.status && terminalStatuses.includes(job.status)) || Boolean(job.pull_request)
}

export function useAgenticAutofixJobPoll(
  owner: string,
  repo: string,
  jobId: string | null,
  onError?: (message: string) => void,
) {
  const path = jobId ? `/agents/swe/v1/jobs/${owner}/${repo}/${jobId}` : null

  const {data} = useQuery({
    queryKey: ['agentic-autofix-job', path],
    queryFn: async () => {
      if (!path) throw new Error('Missing polling path')
      const response = await fetchCAPI({
        path,
        method: 'GET',
      })
      const job = (await response.json()) as AgenticAutofixJob
      if (isTerminal(job) && job.error?.message) {
        onError?.(job.error.message)
      }
      return job
    },
    enabled: !!path,
    refetchInterval: (query: {state: {data?: AgenticAutofixJob}}) => {
      const job = query.state.data
      if (job && isTerminal(job)) return false
      return 1000
    },
  })

  const isTerminalJob = data ? isTerminal(data) : false
  const terminalErrorMessage = isTerminalJob ? data?.error?.message : null

  return {
    errorMessage: terminalErrorMessage ?? null,
    isComplete: isTerminalJob && !terminalErrorMessage,
  }
}
